import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router';
import { useEffect } from 'react';
import { NoAccess } from '@/components/custom/no-access';
import { useSecondarySidebarSlot } from '@/components/layout/secondary-sidebar-slot';
import { useSidebar } from '@/components/ui/sidebar';
import { useMe } from '@/lib/me';
import { setupNav } from '@/lib/navigation';

export const Route = createFileRoute('/_authenticated/setup')({
  component: SetupLayout,
});

function SetupLayout() {
  const location = useLocation();
  const { permissions, loading } = useMe();
  const visible = setupNav.filter((item) => permissions.has(item.permission));
  const { setOpen } = useSidebar();

  // Every time Setup opens: the main nav rail auto-collapses to give the
  // two-column Setup layout room. Fires once per mount of this layout route
  // (entering `/setup` from elsewhere) — not on every sub-page navigation
  // within Setup, and not re-forced afterward, so a user who re-expands the
  // rail while browsing Setup keeps it open until they leave and come back.
  useEffect(() => {
    setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the user can't see any setup section, surface NoAccess at the layout
  // boundary so direct links to /setup don't silently render an empty side
  // panel — and no secondary column either. Wait for /me to load first so we
  // don't flash NoAccess (or briefly hide the column) before permissions land.
  const showNoAccess = !loading && visible.length === 0;

  // Published into `AppLayout`'s secondary-sidebar slot rather than rendered
  // here directly — see `secondary-sidebar-slot.tsx` for why. `h-full` so it
  // fills the full-height row `AppShellBody` gives it (the column now spans
  // the whole viewport height, clear of the fixed `AppHeader`, instead of
  // being pushed down to clear it like the routed page content is).
  useSecondarySidebarSlot(
    showNoAccess ? null : (
      <aside className="flex h-full w-56 shrink-0 flex-col border-r bg-sidebar text-sidebar-foreground">
        {/* Same 11px letterspaced caps as the sidebar's group label, so the two
            nav columns read as one system. */}
        <div className="px-3 py-4">
          <h2 className="text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
            Setup
          </h2>
        </div>
        <nav className="flex flex-col gap-0.5 px-2">
          {visible.map((item) => {
            const active =
              location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
            return (
              <Link
                key={item.to}
                to={item.to}
                // Shared with the main sidebar's own rows — see the
                // `[data-nav='item']` block in globals.css for the default/
                // hover/selected palette (geometry, color, lime hover fill).
                // This Link isn't a sidebar primitive, but the attribute is
                // exactly what lets it "look identical" to one.
                data-nav="item"
                data-active={active}
                className="flex items-center"
              >
                <item.icon className="shrink-0" />
                <span className="truncate">{item.title}</span>
              </Link>
            );
          })}
        </nav>
      </aside>
    ),
  );

  if (showNoAccess) {
    return <NoAccess permission="activity.view" resource="Setup" />;
  }

  return <Outlet />;
}
